/**
 * lib/cards/build-runtime-layout.js — Shared runtime layout builder.
 *
 * Chunk 2 (Apr 24 2026): extracted from template-minimal.js and
 * template-premium.js so that both the jsPDF renderer (print path)
 * and the Konva renderer (editor/preview path) consume the SAME
 * layout object. If these drift, preview won't match print.
 *
 * Exports:
 *   buildMinimalLayout(tenant, staff)   → { layout, derived }
 *   buildPremiumLayout(tenant, staff)   → { layout, derived }
 *
 * `layout` is the JSON schema object consumed by render-layout.js
 * (and by the Konva adapter).
 *
 * `derived` contains the knob-resolved geometry and colours the
 * caller needs for the procedural passes (wave band, banner,
 * mountain range, footer strip) that AREN'T declarative JSON
 * elements. The jsPDF renderer uses these; Konva preview renders
 * them through parallel Konva shape helpers in konva-renderers.js.
 *
 * Pure functions — no image loading, no QR generation. Async content
 * is resolved by the caller and supplied to the renderer separately
 * as a contentMap keyed by element id.
 */

import { CR80, applyDisplayNameMode, resolveBrandColor, stripProtocol } from './shared.js';

/* ─────────────────────────────────────────────────────────────── */
/*  Knob preset tables — Premium only                               */
/* ─────────────────────────────────────────────────────────────── */

const PHOTO_PRESETS = {
  small:  { w: 20, h: 25 },
  medium: { w: 24, h: 30 },
  large:  { w: 28, h: 34 },
};

const NAME_FONT_PRESETS = {
  small:  10,
  medium: 13,
  large:  16,
};

function pick(value, presets, fallback) {
  return presets[value] !== undefined ? presets[value] : presets[fallback];
}

/* ─────────────────────────────────────────────────────────────── */
/*  Minimal layout builder                                          */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Build the Minimal runtime layout + procedural-pass descriptors.
 *
 * Geometry is fixed (no Minimal-side knobs). Text elements use
 * baselineAnchor:true with exact jsPDF baseline y-values to preserve
 * byte-identical print output with the pre-Chunk-1 code.
 *
 * @param {object} tenant  tenant fields including display_name_mode,
 *                         card_company_name, logo_url, public_phone,
 *                         public_website, brand_color
 * @param {object} staff   { first_name, last_name, role, photo_url }
 * @returns {{ layout: object, derived: object }}
 */
export function buildMinimalLayout(tenant, staff) {
  const brandColor = resolveBrandColor(tenant.brand_color);
  const companyText = (tenant.card_company_name || tenant.name || '').trim();
  const displayName = applyDisplayNameMode(
    staff.first_name,
    staff.last_name,
    tenant.display_name_mode || 'full',
  );
  const footerParts = [
    tenant.public_phone,
    stripProtocol(tenant.public_website),
  ].filter(Boolean);

  const layout = {
    version: 1,
    basedOnTemplate: 'minimal',
    canvas: {
      width: CR80.widthMm,
      height: CR80.heightMm,
      units: 'mm',
      background: '#FFFFFF',
    },
    elements: [
      {
        id: 'el_logo',
        type: 'dynamic_logo',
        x: 4, y: 2, w: 34, h: 10,
        z: 10,
        locked: false,
        props: { fit: 'contain' },
      },
      {
        id: 'el_staff_photo',
        type: 'dynamic_photo',
        x: 4, y: 16, w: 20, h: 20,
        z: 10,
        locked: false,
        props: { fit: 'cover' },
      },
      {
        id: 'el_name',
        type: 'dynamic_name',
        x: 28, y: 22, w: 32, h: 5,
        z: 10,
        locked: false,
        props: {
          fontSize: 13,
          fontWeight: 'bold',
          color: '#0E1A15',
          align: 'left',
          baselineAnchor: true,
          truncate: true,
        },
      },
      {
        id: 'el_role',
        type: 'dynamic_role',
        x: 28, y: 27.5, w: 32, h: 4,
        z: 10,
        locked: false,
        props: {
          fontSize: 9,
          fontWeight: 'normal',
          color: '#3C4842',
          align: 'left',
          baselineAnchor: true,
          truncate: true,
        },
      },
      {
        id: 'el_qr',
        type: 'dynamic_qr',
        x: 63, y: 16, w: 20, h: 20,
        z: 10,
        locked: false,
        props: {
          errorCorrectionLevel: 'H',
          dark: '#000000',
          light: '#FFFFFF',
        },
      },
      {
        id: 'el_qr_caption',
        type: 'static_text',
        x: 63, y: 38.5, w: 20, h: 2.5,
        z: 10,
        locked: false,
        props: {
          text: 'SCAN TO VERIFY',
          fontSize: 6,
          fontWeight: 'bold',
          color: '#6B7A70',
          align: 'center',
          baselineAnchor: true,
          charSpace: 0.5,
        },
      },
      {
        id: 'el_footer_text',
        type: 'dynamic_footer_text',
        x: 0, y: 52.2, w: CR80.widthMm, h: 4,
        z: 20,
        locked: false,
        props: {
          fontSize: 7,
          fontWeight: 'normal',
          color: '#FFFFFF',
          align: 'center',
          baselineAnchor: true,
          truncate: false,
        },
      },
    ],
  };

  const derived = {
    brandColor,
    companyText,
    displayName,
    footerText: footerParts.join('  ·  '),
    hasFooter: footerParts.length > 0,
    waveBand: { topY: 49 },        // drawWaveBand(pdf, brandColor, 49)
    logoFallback: {
      text: companyText || 'Verified Staff',
      x: 4, y: 9,                   // baseline
      fontSize: 12,
      fontWeight: 'bold',
      color: '#0E1A15',
    },
    photoPlaceholder: {
      x: 4, y: 16, w: 20, h: 20,
    },
  };

  return { layout, derived };
}

/* ─────────────────────────────────────────────────────────────── */
/*  Premium layout builder                                          */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Build the Premium runtime layout + procedural-pass descriptors.
 *
 * Layout is knob-aware: card_photo_size, card_name_font_size affect
 * element geometry. Caller still handles the procedural passes
 * (banner image vs mountain fallback, brand-colour footer strip)
 * using the descriptors in `derived`.
 */
export function buildPremiumLayout(tenant, staff) {
  const photoSize = pick(tenant.card_photo_size, PHOTO_PRESETS, 'medium');
  const nameFontSize = pick(tenant.card_name_font_size, NAME_FONT_PRESETS, 'medium');
  const footerMode = tenant.card_footer_text_color || 'auto';
  const bannerOpacity = typeof tenant.card_banner_opacity === 'number'
    ? Math.max(0, Math.min(100, tenant.card_banner_opacity)) / 100
    : 1.0;

  const brandColor = resolveBrandColor(tenant.brand_color);
  const companyText = (tenant.card_company_name || tenant.name || '').trim();
  const displayName = applyDisplayNameMode(
    staff.first_name,
    staff.last_name,
    tenant.display_name_mode || 'full',
  );
  const footerParts = [
    tenant.public_phone,
    stripProtocol(tenant.public_website),
  ].filter(Boolean);

  // Geometry — mirrors template-premium.js content pass
  const photoX = 3;
  const photoY = 4;
  const photoW = photoSize.w;
  const photoH = photoSize.h;

  const contentX = photoX + photoW + 4;
  const contentRightBound = CR80.widthMm - 2;

  const qrSize = 20;
  const qrX = contentRightBound - qrSize;
  const qrY = 12;
  const captionY = 10;

  const textColRightBound = qrX - 3;
  const textColWidth = textColRightBound - contentX;

  const logoBandY = 4;
  const logoMaxH = 7;
  const logoMaxW = Math.min(textColWidth, 38);

  const nameY = 18;
  const roleY = nameY + Math.max(4, nameFontSize * 0.4);

  const bannerTopY = 41;
  const bannerHeight = 9;
  const footerY = 50;

  const layout = {
    version: 1,
    basedOnTemplate: 'premium',
    canvas: {
      width: CR80.widthMm,
      height: CR80.heightMm,
      units: 'mm',
      background: '#FFFFFF',
    },
    elements: [
      {
        id: 'el_staff_photo',
        type: 'dynamic_photo',
        x: photoX, y: photoY, w: photoW, h: photoH,
        z: 10,
        locked: false,
        props: { fit: 'cover' },
      },
      {
        id: 'el_logo',
        type: 'dynamic_logo',
        x: contentX, y: logoBandY, w: logoMaxW, h: logoMaxH,
        z: 10,
        locked: false,
        props: { fit: 'contain' },
      },
      {
        id: 'el_name',
        type: 'dynamic_name',
        x: contentX, y: nameY, w: textColWidth, h: 6,
        z: 10,
        locked: false,
        props: {
          fontSize: nameFontSize,
          fontWeight: 'bold',
          color: '#0E1A15',
          align: 'left',
          baselineAnchor: true,
          truncate: true,
        },
      },
      ...(staff.role ? [{
        id: 'el_role',
        type: 'dynamic_role',
        x: contentX, y: roleY, w: textColWidth, h: 4,
        z: 10,
        locked: false,
        props: {
          fontSize: 7,
          fontWeight: 'bold',
          color: 'brand',
          align: 'left',
          baselineAnchor: true,
          uppercase: true,
          charSpace: 0.4,
          truncate: true,
        },
      }] : []),
      {
        id: 'el_qr_caption',
        type: 'static_text',
        x: qrX, y: captionY, w: qrSize, h: 2.5,
        z: 10,
        locked: false,
        props: {
          text: 'SCAN TO VERIFY',
          fontSize: 6,
          fontWeight: 'bold',
          color: 'brand',
          align: 'center',
          baselineAnchor: true,
          charSpace: 0.2,
        },
      },
      {
        id: 'el_qr',
        type: 'dynamic_qr',
        x: qrX, y: qrY, w: qrSize, h: qrSize,
        z: 10,
        locked: false,
        props: {
          errorCorrectionLevel: 'H',
          dark: '#000000',
          light: '#FFFFFF',
        },
      },
    ],
  };

  const derived = {
    brandColor,
    companyText,
    displayName,
    footerText: footerParts.join('  ·  '),
    hasFooter: footerParts.length > 0,
    footerMode,
    bannerOpacity,
    banner: {
      topY: bannerTopY,
      height: bannerHeight,
      imageUrl: tenant.card_bottom_image_url || null,
      mountainFallback: {
        topY: bannerTopY - 3,
        showBirds: true,
      },
    },
    footerStrip: {
      topY: footerY,
      height: CR80.heightMm - footerY,
      fill: brandColor,
      textY: footerY + 2.8, // baseline
      fontSize: 6.5,
      fontWeight: 'normal',
    },
    logoFallback: {
      text: companyText,
      x: contentX, y: logoBandY + 5, // baseline
      fontSize: 10,
      fontWeight: 'bold',
      color: '#0E1A15',
    },
    photoPlaceholder: {
      x: photoX, y: photoY, w: photoW, h: photoH,
      fontSize: photoW > 25 ? 22 : 18,
    },
  };

  return { layout, derived };
}

/* ─────────────────────────────────────────────────────────────── */
/*  Footer colour resolver — Premium                                */
/*                                                                  */
/*  Takes the resolved 'light' | 'dark' string (from shared.js's    */
/*  contrastingTextColor) and the knob mode, returns RGB. Both the  */
/*  PDF template and Konva preview call contrastingTextColor them-  */
/*  selves against brandColor and pass the result here, so we don't */
/*  re-implement luminance.                                         */
/* ─────────────────────────────────────────────────────────────── */

export function resolvePremiumFooterColor(mode, autoContrast) {
  switch (mode) {
    case 'white':
      return { r: 255, g: 255, b: 255 };
    case 'dark':
      return { r: 20, g: 30, b: 28 };
    case 'brand':
      return autoContrast === 'light' ? { r: 255, g: 255, b: 255 } : { r: 20, g: 30, b: 28 };
    case 'auto':
    default:
      return autoContrast === 'light' ? { r: 255, g: 255, b: 255 } : { r: 20, g: 30, b: 28 };
  }
}
